import { useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  useGetHealthOrganisationsQuery,
  useGetSpecimenAuditAnalyticsQuery,
  useGetSpecimenAuditEventsQuery,
  useLazyGetSpecimenAuditEventsQuery,
} from "../../services/api";
import type { SpecimenAuditEvent } from "../../services/api";
import { colors } from "../../theme/colors";
import HeaderBar from "./components/HeaderBar";
import FiltersBar from "./components/FiltersBar";
import KpiStrip from "./components/KpiStrip";
import ActivityFeed from "./components/ActivityFeed";
import AnalyticsBand from "./components/AnalyticsBand";
import {
  buildAnalyticsQueryParams,
  buildEventsQueryParams,
  buildSpecimenAuditCsv,
  CSV_EXPORT_ROW_CAP,
  defaultFilters,
  downloadCsv,
} from "./utils";
import type { HealthOrgOption, SpecimenTrackingFilters } from "./utils";
// Plain react-redux hooks plus a type-only RootState import: pulling the store
// module in at runtime would drag the whole API slice into every consumer.
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../store";
import { setSpecimenTrackingFilters } from "../../store/specimenTracking";

const cardSx = {
  border: `1px solid ${colors.grey}`,
  borderRadius: "8px",
  backgroundColor: "#ffffff",
  p: 2.5,
  mt: 2
} as const;

const SpecimenTracking = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedFilters = useSelector(
    (state: RootState) => state.specimenTracking.filters
  );

  // Opening a timeline unmounts this page, so seed from the last applied set
  // rather than resetting to defaults on the way back. Local state stays the
  // render source; the store just holds the snapshot to come back to.
  const [filters, setLocalFilters] = useState<SpecimenTrackingFilters>(
    () => savedFilters ?? defaultFilters()
  );

  const setFilters = useCallback(
    (next: SpecimenTrackingFilters) => {
      setLocalFilters(next);
      dispatch(setSpecimenTrackingFilters(next));
    },
    [dispatch]
  );

  const { data: healthOrgData } = useGetHealthOrganisationsQuery({
    is_active: true,
  });
  const healthOrgOptions: HealthOrgOption[] = healthOrgData?.items ?? [];

  const hasHealthOrg = filters.healthOrgId != null;

  const {
    data: eventsData,
    isFetching: eventsFetching,
    isError: eventsError,
  } = useGetSpecimenAuditEventsQuery(
    buildEventsQueryParams(filters as typeof filters & { healthOrgId: number }, {
      page: 1,
      page_size: 50,
    }),
    { skip: !hasHealthOrg }
  );

  const {
    data: analyticsData,
    isFetching: analyticsFetching,
    isError: analyticsError,
  } = useGetSpecimenAuditAnalyticsQuery(
    buildAnalyticsQueryParams(filters as typeof filters & { healthOrgId: number }),
    { skip: !hasHealthOrg }
  );

  const [fetchEventsForExport] = useLazyGetSpecimenAuditEventsQuery();

  const [exportNotice, setExportNotice] = useState<{
    text: string;
    severity: "warning" | "error";
  }>();

  const handleExport = async () => {
    if (!hasHealthOrg) return;

    setExportNotice(undefined);
    const healthOrgLabel =
      healthOrgOptions.find((org) => org.id === filters.healthOrgId)
        ?.name ?? "All";

    try {
      const result = await fetchEventsForExport(
        buildEventsQueryParams(filters as typeof filters & { healthOrgId: number }, {
          page: 1,
          page_size: CSV_EXPORT_ROW_CAP,
        })
      ).unwrap();

      const { csv, wasTruncated } = buildSpecimenAuditCsv(result.items, {
        healthOrgLabel,
        filters,
        totalCount: result.count,
      });

      downloadCsv(csv, `specimen-tracking-${Date.now()}.csv`);

      if (wasTruncated) {
        setExportNotice({
          severity: "warning",
          text: `Exported the first ${CSV_EXPORT_ROW_CAP} of ${result.count} matching rows. Narrow your filters to export the rest.`,
        });
      }
    } catch {
      // Deliberately not binding or logging the error: the rejection payload is
      // the raw API response, which carries specimen/patient data.
      setExportNotice({
        severity: "error",
        text: "Export failed. Please try again.",
      });
    }
  };

  const events = eventsData?.items ?? [];

  // The audit API has no get-event-by-id endpoint, so the details screen is fed
  // from router state: the clicked event plus every loaded event sharing its
  // specimen QR, which is the timeline it renders.
  const handleViewTimeline = (event: SpecimenAuditEvent) => {
    const timeline = event.specimen_qr
      ? events.filter((item) => item.specimen_qr === event.specimen_qr)
      : [event];

    // That feed is one filtered page, so the timeline is a subset of the
    // specimen's history whenever rows were left unfetched or a facet is
    // narrowing results. Tell the details page, so it can say so rather than
    // claiming to show everything.
    const isTruncated = (eventsData?.count ?? 0) > events.length;
    const isFiltered = Boolean(
      filters.search ||
      filters.statusTransition ||
      filters.discrepancy ||
      filters.severity
    );

    navigate(`/specimen-tracking/timeline/${event.id}`, {
      state: {
        event,
        timeline,
        isPartialTimeline: isTruncated || isFiltered,
        healthOrgName: healthOrgOptions.find(
          (org) => org.id === filters.healthOrgId
        )?.name,
      },
    });
  };

  return (
    <Box>
      {/* Filters card: organisation + date range + search + facets */}
      <Box sx={cardSx}>
        <HeaderBar
          filters={filters}
          onChange={setFilters}
          healthOrgOptions={healthOrgOptions}
          onExport={handleExport}
          exportDisabled={eventsFetching || !hasHealthOrg}
        />
        <Box
          sx={{ borderTop: `1px solid ${colors.lighterGrey}`, my: 2 }}
          component="hr"
        />
        <FiltersBar filters={filters} onChange={setFilters} />
      </Box>

      {exportNotice && (
        <Typography
          role={exportNotice.severity === "error" ? "alert" : "status"}
          sx={{
            color: exportNotice.severity === "error" ? colors.red : "#9A3412",
            mt: 1.5,
          }}
        >
          {exportNotice.text}
        </Typography>
      )}

      {!hasHealthOrg ? (
        <Box
          sx={{
            ...cardSx,
            mt: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: colors.lightestGrey,
          }}
        >
          <InfoOutlinedIcon sx={{ color: colors.grey }} />
          <Typography sx={{ color: colors.dark }}>
            Select a health organisation to view specimen tracking data.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mt: 3 }}>
            <KpiStrip
              analytics={analyticsData}
              isLoading={analyticsFetching}
              isError={analyticsError}
            />
          </Box>

          <Box sx={{ mt: 3, width: "100%", minWidth: 0 }}>
            <Box sx={cardSx}>
              <ActivityFeed
                events={events}
                isLoading={eventsFetching}
                isError={eventsError}
                onRowClick={handleViewTimeline}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 500, mb: 1.5 }}>
              Analytics
            </Typography>
            <AnalyticsBand analytics={analyticsData} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default SpecimenTracking;
